import { localize, Logger } from '@saviynt/common';
import moment from 'moment';

import { DATE_COLUMNS } from '../../../utils/constant';
import { certsFormattedMessage, getMessageKey } from '../../../utils/utils';

import {
  ADDED_FILTER,
  APPLIED_TO_EO_TYPE_AND_ORDER,
  APPLIED_TO_TYPE_AND_ORDER,
  COUNT_DEFAULT_FILTERS,
  COUNT_SELECTED_FILTERS,
  REMOVED_FILTER,
  UNCHANGED,
} from './constant';

// First argument should be previous state, and second argument should be updated state
export const compareTwoStatesForFilter = (state, updatedState) => {
  if (state.length === updatedState.length) {
    return {
      state: UNCHANGED,
      filter: null,
    };
  }

  if (state.length > updatedState.length) {
    return {
      state: REMOVED_FILTER,
      filter: state.filter(
        (item1) =>
          !updatedState.some(
            (item2) => item2.key === item1.key && item2.value === item1.value
          )
      ),
    };
  }

  return {
    state: ADDED_FILTER,
    filter: updatedState.filter(
      (item1) =>
        !state.some(
          (item2) => item2.key === item1.key && item2.value === item1.value
        )
    ),
  };
};

/**
 *
 * @param {*} obj object to be sorted
 * @returns  sorted object
 */
export const sortObjectByKeys = (obj, priorityKeys = []) => {
  const sortedObj = {};

  if (!obj) {
    return sortedObj;
  }

  // First add prioritized keys in order
  priorityKeys.forEach((key) => {
    if (obj[key]) {
      sortedObj[key] = obj[key];
    }
  });

  // Get all other keys and sort them alphabetically
  const otherKeys = Object.keys(obj)
    .filter((key) => !priorityKeys.includes(key))
    .sort();

  // Iterate through the other keys and add to the sorted object
  otherKeys.forEach((key) => {
    sortedObj[key] = obj[key];
  });

  return sortedObj;
};

const getAppliedToText = (columnPickerList, key, l, isEOCampaign = false) => {
  const appliedToList = [];

  // TODO: @Jacob, @Sagar, CR-17904, Testing and dev env is complaining about columnPickerList, but only sometimes.
  if (!columnPickerList || typeof columnPickerList !== 'object') {
    Logger.warn('Invalid columnPickerList:', columnPickerList);

    return 'Applied to: None';
  }

  Object.entries(columnPickerList).forEach((column) => {
    const appliedToName = column[0];
    const appliedToObject = column[1];

    if (
      Object.hasOwnProperty.call(appliedToObject, key) &&
      !appliedToList.includes(appliedToName)
    ) {
      appliedToList.push(appliedToName);
    }
  });

  const sortedAppliedToList = appliedToList.sort((a, b) => {
    if (isEOCampaign) {
      return (
        APPLIED_TO_EO_TYPE_AND_ORDER.indexOf(a) -
        APPLIED_TO_EO_TYPE_AND_ORDER.indexOf(b)
      );
    }

    return (
      APPLIED_TO_TYPE_AND_ORDER.indexOf(a) -
      APPLIED_TO_TYPE_AND_ORDER.indexOf(b)
    );
  });

  const types = `${sortedAppliedToList
    ?.map((rowType) => l(rowType))
    .join(', ')}`;

  return certsFormattedMessage(l('Applied to: {types}', { types }), { types });
};

const extractDate = (key) => {
  const match = key.match(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/);

  return match ? match[0] : null;
};

// TODO: @Jacob, @Vishnu CR-17904, Dates from BE causing depriciation warning in Jest test results, Parse and Sanitize from Front End required. Might want a ticket for this.
export const reCreateFilterOptions = (filterKey, options, baseSettings) => {
  const tempOptions = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const key in options) {
    if (Object.prototype.hasOwnProperty.call(options, key)) {
      let fieldValue = key;

      if (
        DATE_COLUMNS.includes(filterKey) &&
        key.trim() !== '' &&
        key !== 'NoResponse'
      ) {
        const extractedDate = extractDate(key); // Extract valid date string

        if (extractedDate) {
          const date = moment(extractedDate, 'YYYY-MM-DD HH:mm:ss', true); // Strict format parsing

          if (date.isValid()) {
            fieldValue = date
              .utc()
              .local()
              .format(
                `${baseSettings.globalDateFormat?.toUpperCase()} hh:mm:ss`
              );
          } else {
            Logger.warn(`Invalid date format: ${key}`);
            fieldValue = key; // Fallback to original value
          }
        } else {
          Logger.warn(`Date not found in key: ${key}`);
          fieldValue = key;
        }
      }

      if (key.trim() === '') fieldValue = 'Blank';

      tempOptions.push({
        label: fieldValue,
        value: `${key}`,
        isChecked: false,
      });
    }
  }

  return tempOptions;
};

export const sanitizeAdvancedFilters = (
  filters,
  baseSettings,
  intl,
  messages,
  l,
  isEOCampaign = false
) => {
  const { valueCountMap, messageKeys } = filters;

  let COUNT_OF_FIXED_FILTERS = Math.min(
    COUNT_SELECTED_FILTERS,
    COUNT_DEFAULT_FILTERS
  );
  let count = 0;
  const tempFilters = [];

  const priorityKeys = [];

  Object.values(baseSettings?.columnPickerList || {}).forEach((item) => {
    if (item) {
      const firstKey = Object.keys(item)[0];

      if (firstKey !== undefined) {
        priorityKeys.push(firstKey);
      }
    }
  });

  const sortedValueCountMap = sortObjectByKeys(valueCountMap, priorityKeys);

  COUNT_OF_FIXED_FILTERS =
    priorityKeys.length > COUNT_DEFAULT_FILTERS
      ? priorityKeys.length
      : COUNT_OF_FIXED_FILTERS;

  // eslint-disable-next-line no-restricted-syntax
  for (const key in sortedValueCountMap) {
    if (Object.prototype.hasOwnProperty.call(sortedValueCountMap, key)) {
      const options = reCreateFilterOptions(
        key,
        sortedValueCountMap[key],
        baseSettings
      );

      tempFilters.push({
        key: `${key}`,
        label: `${
          messages[messageKeys[key]] || localize(intl, getMessageKey(key))
        }`,
        isActive: count < COUNT_OF_FIXED_FILTERS,
        isDefault: count < COUNT_OF_FIXED_FILTERS && false,
        isCustom: false,
        appliedTo: getAppliedToText(
          baseSettings.columnPickerList,
          key,
          l,
          isEOCampaign
        ),
        initialCount: options?.length || 0,
        options,
      });

      count += 1;
    }
  }

  return tempFilters;
};

export const getActiveFiltersCount = (filters) =>
  filters.filter((filter) => filter.isActive === true && !filter.isCustom)
    .length;

// to check enable save button for personalization
export const isPersonalizationSaveEnabled = (firstArray, secondArray) => {
  // when personlized values are not exist
  if (!Array.isArray(firstArray)) {
    if (secondArray.length === 0) {
      return false;
    }

    return true;
  }

  const values1 = firstArray?.map((item) => item.value).sort();
  const values2 = secondArray?.map((item) => item.value).sort();

  if (values1?.length !== values2?.length) {
    return true;
  }

  return !values1?.every((val, index) => val === values2[index]);
};
